/**
 * Tiny DOM helpers for the overlay screens (DESIGN 7.3 ui/dom.ts): an
 * element builder, show/hide via the `hidden` attribute, buttons that never
 * keep keyboard focus (DESIGN 6.1: the UI must not steal Space/Enter/Tab
 * from the game), element glyph chips, toasts and guarded localStorage.
 */

import type { Element as GameElement } from '@shared/index.js';
import { ELEMENT_KEY } from './spellbookData.js';
import { WOVEN_TITLE } from './wovenLabels.js';

export type Attrs = Record<string, string | number | boolean | EventListener | undefined | null>;
export type Child = Node | string | number | null | undefined | false | Child[];

/** What every screen's mount() returns (CROSS-PACKAGE CONTRACTS). */
export interface Screen {
  show(): void;
  hide(): void;
  dispose(): void;
}

function append(parent: Node, child: Child): void {
  if (child === null || child === undefined || child === false) return;
  if (Array.isArray(child)) {
    for (const c of child) append(parent, c);
    return;
  }
  parent.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
}

/**
 * `el('div', { class: 'card', onclick: fn }, 'text', otherNode)`. Keys that
 * start with "on" register listeners; boolean true sets an empty attribute,
 * false/null/undefined skip it.
 */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === undefined || value === null || value === false) continue;
      if (key.startsWith('on') && typeof value === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), value);
      } else if (key === 'class') {
        node.className = String(value);
      } else if (value === true) {
        node.setAttribute(key, '');
      } else {
        node.setAttribute(key, String(value));
      }
    }
  }
  for (const c of children) append(node, c);
  return node;
}

export function show(node: HTMLElement): void {
  node.hidden = false;
}

export function hide(node: HTMLElement): void {
  node.hidden = true;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/**
 * A button that drops focus after a mouse or touch click so a later Space /
 * Enter reaches the game instead of re-triggering it. A keyboard activation
 * (`ev.detail` is 0) keeps focus so the menu can be walked on. Tab reaches
 * it only where input/tabFocus.ts lets Tab move focus (never mid-match).
 */
let buttonClickHook: (() => void) | null = null;

/** Something to run on every button press (main.ts plays the UI click); dom.ts stays free of audio imports. */
export function setButtonClickHook(fn: (() => void) | null): void {
  buttonClickHook = fn;
}

export function button(label: Child, onClick: (ev: MouseEvent) => void, cls = 'btn'): HTMLButtonElement {
  const b = el('button', { type: 'button', class: cls }, label);
  b.addEventListener('click', (ev) => {
    buttonClickHook?.();
    onClick(ev);
    if (ev.detail > 0) b.blur();
  });
  return b;
}

/** The control keyboard focus is on, if it got there by keyboard: what a dialog hands focus back to when it closes. */
export function keyboardFocus(): HTMLElement | null {
  const a = document.activeElement;
  return a instanceof HTMLElement && a !== document.body && a.matches(':focus-visible') ? a : null;
}

/** Hand focus back to `prev` once a dialog is gone, unless the player already moved it somewhere outside `within`. */
export function restoreFocus(prev: HTMLElement | null, within: HTMLElement): void {
  if (!prev || !prev.isConnected) return;
  const a = document.activeElement;
  if (a === null || a === document.body || within.contains(a)) prev.focus({ preventScroll: true });
}

/** Move focus onto a dialog's panel itself (tabindex -1, no ring): its label is announced and the next Tab reaches its first control. */
export function focusPanel(panel: HTMLElement): void {
  panel.tabIndex = -1;
  panel.focus({ preventScroll: true });
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/** Keep Tab (and Shift+Tab) cycling inside an open dialog. Call from its keydown handler. */
export function trapTab(ev: KeyboardEvent, panel: HTMLElement): void {
  if (ev.code !== 'Tab') return;
  const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) {
    ev.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (!panel.contains(active) || active === panel) {
    ev.preventDefault();
    (ev.shiftKey ? last : first).focus();
  } else if (ev.shiftKey && active === first) {
    ev.preventDefault();
    last.focus();
  } else if (!ev.shiftKey && active === last) {
    ev.preventDefault();
    first.focus();
  }
}

/** A key cap, e.g. keycap('Shift + LMB'). */
export function keycap(text: string): HTMLElement {
  return el('kbd', { class: 'key' }, text);
}

/** An element glyph chip in the element's colour, labelled with its key (QF for steam, QR for ice). */
export function chip(element: GameElement, label?: string, size: 'sm' | 'md' | 'lg' = 'md'): HTMLElement {
  return el('span', { class: `chip el-${element} chip-${size}`, title: elementTitle(element) }, label ?? ELEMENT_KEY[element]);
}

const ELEMENT_TITLE: Readonly<Record<GameElement, string>> = {
  water: 'Water (Q)', life: 'Life (W)', shield: 'Shield (E)', cold: 'Cold (R)', lightning: 'Lightning (A)',
  arcane: 'Arcane (S)', earth: 'Earth (D)', fire: 'Fire (F)', steam: 'Steam (Q then F)', ice: 'Ice (Q then R)',
  mud: 'Mud (Q then D)', lava: 'Lava (F then D)', spring: 'Spring (Q then W)',
  ...WOVEN_TITLE,
};

export function elementTitle(element: GameElement): string {
  return ELEMENT_TITLE[element];
}

// ------------------------------------------------------------------ toasts

export type ToastKind = 'info' | 'error' | 'success';

let toastRoot: HTMLElement | null = null;

function ensureToastRoot(): HTMLElement {
  if (toastRoot && toastRoot.isConnected) return toastRoot;
  const existing = document.getElementById('toasts');
  toastRoot = existing ?? el('div', { id: 'toasts' });
  if (!toastRoot.isConnected) document.body.appendChild(toastRoot);
  return toastRoot;
}

/** A transient message in the top-centre stack; errors stay a little longer. */
export function toast(text: string, kind: ToastKind = 'info', ms = kind === 'error' ? 5000 : 3000): void {
  const root = ensureToastRoot();
  const node = el('div', { class: `toast toast-${kind}`, role: kind === 'error' ? 'alert' : 'status' }, text);
  root.appendChild(node);
  while (root.children.length > 5) root.firstElementChild?.remove();
  window.setTimeout(() => {
    node.classList.add('toast-out');
    window.setTimeout(() => node.remove(), 400);
  }, ms);
}

// ------------------------------------------------------------------ storage

/** localStorage read that tolerates privacy modes and quota errors. */
export function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* blocked or full: the setting just does not persist */
  }
}

export function storageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to remove or storage blocked */
  }
}

/** Copy text to the clipboard; resolves false when no clipboard API is reachable. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the selection fallback */
  }
  try {
    const ta = el('textarea', { class: 'clip', readonly: true, tabindex: -1 });
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
